import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LogService } from '@wheere-front/shared-util';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'lib-live-logs-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslocoModule],
  styles: [
    `
      :host {
        display: contents;
      }
      .term {
        background: #0f0f0f;
        color: #e5e7eb;
        font-family: Consolas, 'Courier New', Courier, monospace;
        font-size: 11.5px;
        line-height: 1.45;
        padding: 8px 10px;
        overflow: auto;
        white-space: pre-wrap;
        word-break: break-all;
      }
      .term-line {
        white-space: pre-wrap;
      }
      :host ::ng-deep .term-line .timestamp {
        color: #9ca3af;
      }
      :host ::ng-deep .term-line .brackets {
        color: #6b7280;
      }
    `,
  ],
  template: `
    @if (!suppressed()) {
      @if (!open()) {
        <button
          type="button"
          class="fixed bottom-4 left-4 z-40 flex size-12 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg ring-1 ring-transparent transition-all hover:scale-105 hover:bg-slate-800 dark:bg-slate-700 dark:ring-slate-500 dark:hover:bg-slate-600"
          [attr.aria-label]="'liveLogs.openAria' | transloco: { count: logs().length }"
          [title]="'liveLogs.fabTitle' | transloco"
          (click)="onOpen()">
          <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8 9l3 3-3 3M13 15h3" />
            <rect x="3" y="5" width="18" height="14" rx="2" stroke-linejoin="round" />
          </svg>

          <!-- Unread error indicator — a small pulsing red dot with the count.
             Hidden when there are no unread errors so the FAB stays minimal. -->
          @if (unreadErrors() > 0) {
            <span class="absolute -right-1 -top-1 flex size-5 items-center justify-center">
              <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
              <span
                class="relative inline-flex size-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                {{ unreadErrors() > 99 ? '99+' : unreadErrors() }}
              </span>
            </span>
          }
        </button>
      }

      <!-- ────────────────────────────────────────────────────────────────────
      Expanded panel. Anchored bottom-left, fixed 420px wide (capped at
      viewport width on small screens), 60vh tall. Header keeps the close
      button + line counter; body is the terminal.
      ──────────────────────────────────────────────────────────────────── -->
      @if (open()) {
        <section
          class="fixed bottom-4 left-4 z-40 flex w-[420px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-slate-700 bg-slate-900 shadow-2xl"
          style="height: min(60vh, 600px);"
          role="dialog"
          [attr.aria-label]="'liveLogs.title' | transloco">
          <!-- Header — title, line count, close. Drag-anywhere-to-close not
             implemented (would need a drag handle + state); a single close
             button is enough for the simplest UX. -->
          <header
            class="flex items-center justify-between gap-2 border-b border-slate-700 bg-slate-800 px-3 py-2 text-white">
            <div class="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider">
              <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8 9l3 3-3 3M13 15h3" />
                <rect x="3" y="5" width="18" height="14" rx="2" stroke-linejoin="round" />
              </svg>
              {{ 'liveLogs.title' | transloco }}
              <span class="text-xs font-normal text-slate-400">
                · {{ 'liveLogs.lines' | transloco: { count: logs().length } }}</span
              >
              @if (totalErrors() > 0) {
                <span
                  class="rounded-full bg-red-500/30 px-1.5 py-0.5 text-xs font-bold text-red-300"
                  [title]="'liveLogs.errorCountTitle' | transloco">
                  {{ 'liveLogs.errorChip' | transloco: { count: totalErrors() } }}
                </span>
              }
            </div>
            <button
              type="button"
              class="rounded p-1 text-slate-400 transition-colors hover:bg-slate-700 hover:text-white"
              (click)="onClose()"
              [attr.aria-label]="'liveLogs.closeAria' | transloco"
              [title]="'liveLogs.closeTitle' | transloco">
              <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <!-- Terminal — last MAX_VISIBLE lines for snappy paint. Scroll-to-bottom
             driven by stickToBottom + lastLineCount, same trick as the inside
             session widget. -->
          <div #terminal class="term flex-1" (scroll)="onTerminalScroll($event)">
            @if (visibleLines().length === 0) {
              <div class="flex h-full items-center justify-center text-slate-500">
                {{ 'liveLogs.empty' | transloco }}
              </div>
            } @else {
              @for (html of visibleLines(); track $index) {
                <div class="term-line" [innerHTML]="html"></div>
              }
            }
          </div>
        </section>
      }
    }
  `,
})
export class LiveLogsWidgetComponent implements AfterViewChecked {
  /** The full live log buffer — caller passes the store signal value. The widget
   * shows the tail (last {@link #MAX_VISIBLE} lines) for fast paint. */
  logs = input.required<string[]>();

  /** When {@code true} the widget renders nothing and ignores ERR lines (no
   * auto-open, no badge). Pass {@code activeTab() === 'logs'} from the host so
   * the floating panel disappears when the user is already on the full Logs
   * tab — duplicating the same buffer in two places is just noise. */
  suppressed = input<boolean>(false);

  /** Optional callback for the parent — fires every time a new ERR line lands so
   * the host page can kick off side-effects (e.g. a Toast). The widget itself
   * already handles the visual auto-open. */
  errorDetected = output<string>();

  /** Cap how many lines render at once. The buffer can hold thousands; pasting
   * 5k lines into a single DOM node would jank the panel. We render the tail
   * because the auto-scroll-to-bottom UX expects the most recent lines. */
  readonly #MAX_VISIBLE = 200;

  protected readonly open = signal(false);

  /** Total ERR lines in the current buffer — shown as a chip in the header so the
   * operator sees the failure history at a glance. */
  protected readonly totalErrors = computed(() => this.logs().filter(l => l.includes('ERR')).length);

  /** Errors landed while the panel was closed — drives the red badge on the
   * collapsed FAB. Reset to 0 whenever the panel opens. */
  protected readonly unreadErrors = signal(0);

  /** Set once the user closes the panel (button or Esc). From then on new errors
   * only bump the FAB badge: the panel never pops open by itself again for the
   * life of this widget (i.e. the detail page). Switching tabs keeps it. */
  #autoOpenDisabled = false;

  /** Tail of the buffer, formatted via {@link LogService#formatLogLineRaw} so ANSI
   * codes become coloured spans (same look as the full Logs tab). */
  protected readonly visibleLines = computed<SafeHtml[]>(() => {
    const all = this.logs();
    const tail = all.length > this.#MAX_VISIBLE ? all.slice(-this.#MAX_VISIBLE) : all;
    return tail.map(line => this.#sanitizer.bypassSecurityTrustHtml(this.#logService.formatLogLineRaw(line)));
  });

  #logService = inject(LogService);
  #sanitizer = inject(DomSanitizer);

  /** Reference to the terminal viewport — drives auto-scroll-to-bottom.
   * NB: Angular's {@code viewChild} signal API does not support ES private
   * (`#field`) declarations yet, so we keep this as a {@code private}
   * TypeScript-private field instead. */
  private readonly terminal = viewChild<ElementRef<HTMLDivElement>>('terminal');

  /** Track the last seen total-error count to detect "new error since last frame".
   * This is plain instance state, not a signal — we only mutate it inside the
   * effect that reads {@code totalErrors()}. */
  #lastSeenErrors = 0;

  /** Auto-scroll state. {@code stickToBottom} is true while the user hasn't
   * manually scrolled up; new lines auto-snap to the bottom. {@code forceScrollNext}
   * jumps to the bottom on the next paint regardless (used right after open() so
   * the panel always starts at the most recent line). {@code lastLineCount} lets
   * us detect line growth between AfterViewChecked passes. */
  #stickToBottom = true;
  #forceScrollNext = false;
  #lastLineCount = 0;

  constructor() {
    // Auto-open on the first new ERR. We compare the current totalErrors() count
    // against the previous frame's count: if it grew, an error landed since the
    // last tick. Until the user has closed the panel once, the widget opens
    // itself; after that, new errors only bump the FAB badge. The
    // {@link suppressed} input (user on the dedicated Logs tab) mutes both.
    effect(
      () => {
        const total = this.totalErrors();
        if (this.suppressed()) {
          // Keep the watermark in sync so we don't replay every ERR when the
          // host un-suppresses (e.g. operator switches away from the Logs tab).
          this.#lastSeenErrors = total;
          return;
        }
        if (total > this.#lastSeenErrors) {
          const fresh = total - this.#lastSeenErrors;
          // Forward the latest error line up so the host can fire its own toast
          // / sentry breadcrumb / whatever cross-cutting concern it has.
          const latest = [...this.logs()].reverse().find(l => l.includes('ERR'));
          if (latest) this.errorDetected.emit(latest);
          if (!this.open()) {
            if (this.#autoOpenDisabled) this.unreadErrors.update(c => c + fresh);
            else this.onOpen();
          }
        }
        this.#lastSeenErrors = total;
      },
      { allowSignalWrites: true }
    );

    // Force a snap-to-bottom every time the panel opens (or re-opens after a
    // suppression toggle). Without this, opening the widget mid-stream would
    // land the viewport at scrollTop=0 because the terminal element is fresh.
    effect(
      () => {
        if (this.open()) this.#forceScrollNext = true;
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * Auto-scroll the terminal to its bottom whenever new lines land — but only if
   * the user hasn't manually scrolled up (we respect their position so they can
   * actually read older entries). The {@code forceScrollNext} flag bypasses the
   * stickToBottom check, used on open / right after a clear / when the host
   * toggles suppression off.
   */
  ngAfterViewChecked(): void {
    if (!this.open()) return;
    const el = this.terminal()?.nativeElement;
    if (!el) return;
    const lineCount = this.visibleLines().length;
    const grew = lineCount > this.#lastLineCount;
    if ((this.#stickToBottom && grew) || this.#forceScrollNext) {
      el.scrollTop = el.scrollHeight;
      this.#forceScrollNext = false;
    }
    this.#lastLineCount = lineCount;
  }

  /**
   * Detect whether the user is still pinned to the bottom of the terminal. A
   * 32px slack means a tiny scroll near the bottom (rubber-banding on macOS)
   * doesn't break auto-stick.
   */
  protected onTerminalScroll(e: Event): void {
    const el = e.target as HTMLDivElement;
    this.#stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
  }

  /** Esc closes the panel. We don't bother adding a global shortcut to OPEN it —
   * the FAB is always reachable in the corner. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) this.onClose();
  }

  protected onOpen(): void {
    this.unreadErrors.set(0);
    this.open.set(true);
  }

  protected onClose(): void {
    this.open.set(false);
    this.#autoOpenDisabled = true;
  }
}
